import { Outlet, Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, User, Menu, X, Search } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useState, useEffect } from "react";
import CartDrawer from "../components/store/CartDrawer";
import { api } from "../lib/api";
import { useBrandLogo } from "../lib/logo";

export default function StoreLayout() {
  const { count, setOpen } = useCart();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [cats, setCats] = useState([]);
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const { logoUrl } = useBrandLogo();

  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-stone-900">
      {/* Ribbon */}
      <div className="bg-[#3B2F2F] text-[#FDFBF7] text-xs tracking-widest uppercase overflow-hidden">
        <div className="flex whitespace-nowrap ribbon-marquee py-2 gap-16">
          {Array.from({ length: 2 }).flatMap((_, k) => (
            ["Free shipping over ₹500", "Freshly roasted weekly", "Your daily dose of good coffee",
             "Estate-sourced from Coorg, Chikmagalur & Nilgiris", "Try code WELCOME10 for 10% off"]
          ).map((t, i) => (
            <span key={`${k}-${i}`} className="px-4">✦ {t}</span>
          )))}
        </div>
      </div>

      {/* Nav */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#FDFBF7]/85 border-b border-black/5">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-20">
          <button className="md:hidden" data-testid="mobile-menu-btn"
                  onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X /> : <Menu />}
          </button>
          <Link to="/" data-testid="brand-logo" className="flex items-center gap-3">
            <img src={logoUrl} alt="DailyBeans" className="w-10 h-10 md:w-12 md:h-12 object-contain" />
            <span className="font-serif-display text-2xl md:text-3xl tracking-tight">DailyBeans</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium">
            <NavLink to="/shop" data-testid="nav-shop" className={({isActive})=>isActive?"text-[#3B2F2F]":"text-stone-600 hover:text-[#3B2F2F]"}>Shop All</NavLink>
            {cats.slice(0,4).map(c => (
              <NavLink key={c.slug} to={`/shop/${c.slug}`}
                       data-testid={`nav-cat-${c.slug}`}
                       className={({isActive})=>isActive?"text-[#3B2F2F]":"text-stone-600 hover:text-[#3B2F2F]"}>
                {c.name}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <form onSubmit={(e)=>{e.preventDefault(); navigate(`/shop?q=${encodeURIComponent(q)}`)}}
                  className="hidden lg:flex items-center bg-white/70 border border-stone-200 rounded-full px-3 py-1.5">
              <Search className="w-4 h-4 text-stone-500" />
              <input value={q} onChange={(e)=>setQ(e.target.value)} data-testid="search-input"
                     placeholder="Search coffees, teas…" className="bg-transparent outline-none text-sm px-2 w-48" />
            </form>
            {user ? (
              <div className="flex items-center gap-2">
                <Link to="/account" data-testid="nav-account" className="p-2 rounded-full hover:bg-stone-100"><User className="w-5 h-5" /></Link>
                <button onClick={logout} data-testid="nav-logout" className="text-xs text-stone-500 hover:text-[#3B2F2F]">Sign out</button>
              </div>
            ) : (
              <Link to="/login" data-testid="nav-login" className="text-sm font-medium text-stone-700 hover:text-[#3B2F2F]">Sign in</Link>
            )}
            <button onClick={() => setOpen(true)} data-testid="nav-cart"
                    className="relative rounded-full bg-[#3B2F2F] text-[#FDFBF7] px-4 py-2 flex items-center gap-2 hover:bg-black">
              <ShoppingBag className="w-4 h-4" />
              <span className="text-sm">{count}</span>
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div className="md:hidden border-t border-black/5 px-4 py-4 space-y-3">
            <Link to="/shop" onClick={()=>setMobileOpen(false)} className="block">Shop All</Link>
            {cats.map(c => <Link key={c.slug} to={`/shop/${c.slug}`} onClick={()=>setMobileOpen(false)} className="block text-stone-600">{c.name}</Link>)}
          </div>
        )}
      </header>

      <main><Outlet /></main>

      <footer className="mt-24 bg-[#3B2F2F] text-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 grid md:grid-cols-4 gap-10">
          <div>
            <div className="flex items-center gap-3">
              <img src={logoUrl} alt="DailyBeans" className="w-14 h-14 object-contain bg-[#FDFBF7] rounded-full p-1" />
              <div>
                <div className="font-serif-display text-3xl">DailyBeans</div>
                <div className="text-xs text-[#D4AF37] italic mt-0.5">Est. 2024 · Brewed with care.</div>
              </div>
            </div>
            <p className="text-sm text-[#FDFBF7]/70 leading-relaxed mt-4">Your daily dose of good coffee. Estate-sourced, small-batch roasted, and delivered fresh to your doorstep.</p>
          </div>
          <div>
            <div className="uppercase text-xs tracking-widest text-[#D4AF37] mb-4">Shop</div>
            <ul className="space-y-2 text-sm">
              {cats.slice(0,5).map(c => <li key={c.slug}><Link to={`/shop/${c.slug}`} className="hover:text-[#D4AF37]">{c.name}</Link></li>)}
            </ul>
          </div>
          <div>
            <div className="uppercase text-xs tracking-widest text-[#D4AF37] mb-4">Company</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/about" className="hover:text-[#D4AF37]">About Us</Link></li>
              <li><Link to="/contact" className="hover:text-[#D4AF37]">Contact</Link></li>
              <li><Link to="/faq" className="hover:text-[#D4AF37]">FAQ</Link></li>
              <li><Link to="/shipping" className="hover:text-[#D4AF37]">Shipping</Link></li>
              <li><Link to="/returns" className="hover:text-[#D4AF37]">Returns</Link></li>
              <li><Link to="/privacy" className="hover:text-[#D4AF37]">Privacy</Link></li>
              <li><Link to="/terms" className="hover:text-[#D4AF37]">Terms</Link></li>
            </ul>
          </div>
          <div>
            <div className="uppercase text-xs tracking-widest text-[#D4AF37] mb-4">Newsletter</div>
            <p className="text-sm text-[#FDFBF7]/70 mb-3">Get 10% off your first order.</p>
            <NewsletterForm />
          </div>
        </div>
        <div className="border-t border-white/10 py-6 max-w-7xl mx-auto px-4 md:px-8 text-xs text-[#FDFBF7]/60">
          <div className="uppercase tracking-widest text-[#D4AF37] mb-1" data-testid="footer-managed-by-label">Managed by</div>
          <div className="text-[#FDFBF7]/85 font-medium" data-testid="footer-managed-by-name">DailyBeans Foods &amp; Beverages Pvt Ltd</div>
          <div className="text-[#FDFBF7]/60 mt-0.5" data-testid="footer-managed-by-address">#05, Balaji Serenity, Andhapura, Electronics City 2, Bengaluru 560100</div>
          <div className="text-[#FDFBF7]/60 mt-0.5" data-testid="footer-managed-by-gstin">GSTIN: 29AANCD0102E1Z3</div>
        </div>
        <div className="border-t border-white/10 py-4 text-center text-xs text-[#FDFBF7]/50">© {new Date().getFullYear()} DailyBeans. Brewed with care. Made for you.</div>
      </footer>
      <CartDrawer />
    </div>
  );
}

function NewsletterForm() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/newsletter", { email }); setDone(true); } catch {}
  };
  return done ? (
    <div className="text-sm text-[#D4AF37]" data-testid="newsletter-success">You're on the list ✦</div>
  ) : (
    <form onSubmit={submit} className="flex gap-2">
      <input value={email} onChange={(e)=>setEmail(e.target.value)} type="email" required
             data-testid="newsletter-email" placeholder="your@email.com"
             className="bg-white/10 border border-white/20 rounded-full px-4 py-2 text-sm flex-1 outline-none placeholder:text-[#FDFBF7]/40" />
      <button data-testid="newsletter-submit" className="bg-[#D4AF37] text-[#3B2F2F] rounded-full px-4 py-2 text-sm font-semibold">Join</button>
    </form>
  );
}
